import { useState } from "react";
import { Button, Modal, Input, Select } from "rizzui";
import {
  IColorTag,
  ColorTagColor,
  CreateColorTagDto,
} from "@/utilities/types/ColorTag";
import ColorTagBadge from "./ColorTagBadge";
import { PiPlus, PiTrash, PiPencil } from "react-icons/pi";
import cn from "@/utils/class-names";
import { COLOR_TAG_STYLES } from "@/config/constants";

interface ColorTagManagerProps {
  isOpen: boolean;
  onClose: () => void;
  colorTags: IColorTag[];
  onCreateTag: (data: CreateColorTagDto) => Promise<void>;
  onUpdateTag: (id: string, data: Partial<CreateColorTagDto>) => Promise<void>;
  onDeleteTag: (id: string) => Promise<void>;
}

export default function ColorTagManager({
  isOpen,
  onClose,
  colorTags,
  onCreateTag,
  onUpdateTag,
  onDeleteTag,
}: ColorTagManagerProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingTag, setEditingTag] = useState<IColorTag | null>(null);
  const [formData, setFormData] = useState<CreateColorTagDto>({
    name: ColorTagColor.BLUE,
    description: "",
    color: COLOR_TAG_STYLES[ColorTagColor.BLUE].hex,
  });

  const colorOptions = Object.values(ColorTagColor).map((color) => ({
    label: color.charAt(0).toUpperCase() + color.slice(1),
    value: color,
  }));

  const resetForm = () => {
    setFormData({
      name: ColorTagColor.BLUE,
      description: "",
      color: COLOR_TAG_STYLES[ColorTagColor.BLUE].hex,
    });
    setShowCreateForm(false);
    setEditingTag(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) return;

    setIsLoading(true);
    try {
      if (editingTag) {
        await onUpdateTag(editingTag.id, formData);
      } else {
        await onCreateTag(formData);
      }
      resetForm();
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (tag: IColorTag) => {
    setEditingTag(tag);
    setFormData({
      name: tag.name,
      color: COLOR_TAG_STYLES[tag.name].hex,
      description: tag.description,
    });
    setShowCreateForm(true);
  };

  const handleDelete = async (id: string) => {
    setIsLoading(true);
    try {
      await onDeleteTag(id);
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <div className="p-6">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Manage Color Tags
          </h3>
          <p className="text-sm text-gray-600">
            Create and manage color tags for user categorization
          </p>
        </div>

        {/* Create/Edit Form */}
        {showCreateForm && (
          <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
            <h4 className="text-sm font-medium text-gray-700 mb-3">
              {editingTag ? "Edit Color Tag" : "Create New Color Tag"}
            </h4>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Tag Name"
                  placeholder="Enter tag name"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  required
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Color
                  </label>
                  <Select
                    options={colorOptions}
                    value={colorOptions.find(
                      (option) => option.value === formData.name
                    )}
                    onChange={(option: { value: ColorTagColor }) =>
                      setFormData({
                        ...formData,
                        name: option.value,
                      })
                    }
                    placeholder="Select color"
                  />
                </div>
              </div>

              {/* Color Preview */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Preview:</span>
                <span
                  className={cn(
                    "inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-full border font-medium",
                    COLOR_TAG_STYLES[formData.name]?.bg,
                    COLOR_TAG_STYLES[formData.name]?.text,
                    COLOR_TAG_STYLES[formData.name]?.border
                  )}
                >
                  {formData.description || "Tag Name"}
                </span>
              </div>

              <div className="flex justify-end gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={resetForm}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isLoading || !formData.name.trim()}
                >
                  {editingTag ? "Update Tag" : "Create Tag"}
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Add New Button */}
        {!showCreateForm && (
          <div className="mb-6">
            <Button
              onClick={() => setShowCreateForm(true)}
              className="flex items-center gap-2"
            >
              <PiPlus className="w-4 h-4" />
              Add New Color Tag
            </Button>
          </div>
        )}

        {/* Existing Tags */}
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-700 mb-3">
            Existing Tags ({colorTags.length})
          </h4>
          {colorTags.length > 0 ? (
            <div className="space-y-2">
              {colorTags.map((tag) => (
                <div
                  key={tag.id}
                  className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  <ColorTagBadge colorTag={tag} size="md" />
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(tag)}
                      disabled={isLoading}
                    >
                      <PiPencil className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      color="danger"
                      onClick={() => handleDelete(tag.id)}
                      disabled={isLoading}
                    >
                      <PiTrash className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 italic">
              No color tags created yet
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end">
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
}
